// src/features/academic/services/challengeService.js
import { db } from '../../../services/firebase/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  doc, 
  getDoc, 
  setDoc, 
  addDoc,
  runTransaction, 
  serverTimestamp, 
  increment,
  documentId
} from 'firebase/firestore';

/**
 * Challenge Service
 * Responsible for managing academic challenges, leaderboards, and user scores
 */
const challengeService = {
  /**
   * Fetches daily quiz questions from multiple API sources with fallbacks
   * @returns {Promise<Array>} Array of question objects
   */
  async fetchDailyQuestions() {
    try {
      // Try to get questions from Firestore first (cache)
      const questionsRef = collection(db, 'quizQuestions');
      const today = new Date().toISOString().split('T')[0];
      
      const questionsQuery = query(
        questionsRef,
        where('active', '==', true),
        where('dateCreated', '>=', today),
        orderBy('dateCreated'),
        limit(10)
      );
      
      const snapshot = await getDocs(questionsQuery);
        
      if (!snapshot.empty) {
        console.log('Using cached questions from Firestore');
        const questions = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        return questions;
      }
      
      // If no cached questions, fetch from APIs
      console.log('No cached questions found, fetching from APIs');
      const questions = await this.fetchQuestionsFromAPIs();
      
      // Store fetched questions in Firestore for future use
      try {
        await this.storeQuestionsInFirestore(questions);
      } catch (storeError) {
        console.warn('Error storing questions in Firestore:', storeError);
        // Continue even if storing fails - we still have questions to return
      }
      
      return questions;
    } catch (error) {
      console.error('Error fetching questions:', error);
      throw error;
    }
  },
  
  /**
   * Fetches questions from multiple API sources with fallbacks
   * @returns {Promise<Array>} Array of formatted question objects
   */
  async fetchQuestionsFromAPIs() {
    const apiSources = [
      {
        name: 'TriviaAPI',
        url: 'https://the-trivia-api.com/v2/questions?limit=10',
        formatter: this.formatTriviaAPIQuestions.bind(this)
      },
      {
        name: 'QuizAPI',
        url: 'https://quizapi.io/api/v1/questions?apiKey=YOUR_API_KEY&limit=10',
        formatter: this.formatQuizAPIQuestions.bind(this),
        requiresKey: true // Skip this if no API key
      },
      {
        name: 'OpenTriviaDB',
        url: 'https://opentdb.com/api.php?amount=10&type=multiple',
        formatter: this.formatOpenTriviaQuestions.bind(this)
      }
    ];
    
    for (const apiSource of apiSources) {
      try {
        // Skip APIs that require keys we don't have
        if (apiSource.requiresKey && apiSource.url.includes('YOUR_API_KEY')) {
          continue;
        }
        
        console.log(`Trying ${apiSource.name}...`);
        
        const response = await fetch(apiSource.url);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        const formattedQuestions = await apiSource.formatter(data);
        
        if (formattedQuestions && formattedQuestions.length > 0) {
          console.log(`Successfully fetched ${formattedQuestions.length} questions from ${apiSource.name}`);
          return formattedQuestions;
        }
        
      } catch (error) {
        console.warn(`Error with ${apiSource.name}:`, error.message);
        continue; // Try next API
      }
    }
    
    // If all APIs fail, throw error
    throw new Error('All trivia APIs are currently unavailable. Please try again later.');
  },
  
  /**
   * Format questions from The Trivia API
   * @param {Object} data - API response data
   * @returns {Array} Formatted questions
   */
  async formatTriviaAPIQuestions(data) {
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('Invalid response format');
    }
    
    return data.map((q, index) => {
      // Combine correct answer with incorrect ones and shuffle
      const allAnswers = [q.correctAnswer, ...q.incorrectAnswers];
      const options = this.shuffleArray(allAnswers);
      
      return {
        id: `trivia-api-${Date.now()}-${index}`,
        question: q.question.text || q.question,
        options: options,
        correctAnswer: q.correctAnswer,
        category: q.category,
        difficulty: q.difficulty,
        active: true,
        source: 'triviaapi',
        dateCreated: new Date().toISOString().split('T')[0]
      };
    });
  },
  
  /**
   * Format questions from Quiz API
   * @param {Object} data - API response data
   * @returns {Array} Formatted questions
   */
  async formatQuizAPIQuestions(data) {
    if (!Array.isArray(data) || data.length === 0) {
      throw new Error('Invalid response format');
    }
    
    return data.map((q, index) => {
      // Extract all possible answers
      const options = [];
      const correctAnswers = [];
      
      Object.keys(q.answers).forEach(key => {
        if (q.answers[key]) {
          options.push(q.answers[key]);
          if (q.correct_answers[`${key}_correct`] === 'true') {
            correctAnswers.push(q.answers[key]);
          }
        }
      });
      
      return {
        id: `quiz-api-${Date.now()}-${index}`,
        question: q.question,
        options: this.shuffleArray(options),
        correctAnswer: correctAnswers[0], // Take first correct answer
        category: q.category || 'General Knowledge',
        difficulty: q.difficulty || 'medium',
        active: true,
        source: 'quizapi',
        dateCreated: new Date().toISOString().split('T')[0]
      };
    });
  },
  
  /**
   * Format questions from Open Trivia DB
   * @param {Object} data - API response data
   * @returns {Array} Formatted questions
   */
  async formatOpenTriviaQuestions(data) {
    if (data.response_code !== 0) {
      throw new Error(`API error code: ${data.response_code}`);
    }
    
    if (!data.results || data.results.length === 0) {
      throw new Error('No questions in response');
    }
    
    return data.results.map((q, index) => {
      // Combine correct answer with incorrect ones and shuffle
      const options = [q.correct_answer, ...q.incorrect_answers];
      
      return {
        id: `opentdb-${Date.now()}-${index}`,
        question: this.decodeHtmlEntities(q.question),
        options: this.shuffleArray(options.map(opt => this.decodeHtmlEntities(opt))),
        correctAnswer: this.decodeHtmlEntities(q.correct_answer),
        category: q.category,
        difficulty: q.difficulty,
        active: true,
        source: 'opentriviadb',
        dateCreated: new Date().toISOString().split('T')[0]
      };
    });
  },
  
  /**
   * Utility function to shuffle an array
   * @param {Array} array - Array to shuffle
   * @returns {Array} Shuffled array
   */
  shuffleArray(array) {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  },
  
  /**
   * Decode HTML entities in text
   * @param {string} text - Text with HTML entities
   * @returns {string} Decoded text
   */
  decodeHtmlEntities(text) {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
  },
  
  /**
   * Stores questions in Firestore for future use
   * @param {Array} questions - Array of question objects to store
   * @returns {Promise<void>}
   */
  async storeQuestionsInFirestore(questions) {
    const questionsRef = collection(db, 'quizQuestions');
    
    // Store each question as a new document
    const storePromises = questions.map(question => 
      addDoc(questionsRef, {
        ...question,
        createdAt: serverTimestamp()
      })
    );
    
    await Promise.all(storePromises);
    console.log(`Stored ${questions.length} questions in Firestore`);
  },
  
  /**
   * Checks if a user has completed today's challenge
   * @param {string} userId - User ID to check
   * @returns {Promise<boolean>} Whether user has completed today's challenge
   */
  async hasCompletedTodaysChallenge(userId) {
    try {
      if (!userId) return false;
      
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      
      const scoresRef = collection(db, 'quizScores');
      const scoresQuery = query(
        scoresRef,
        where('userId', '==', userId),
        where('day', '==', today),
        limit(1)
      );
      
      const snapshot = await getDocs(scoresQuery);
        
      return !snapshot.empty;
    } catch (error) {
      console.error('Error checking completion status:', error);
      return false;
    }
  },
  
  /**
   * Submits a user's quiz score
   * @param {Object} scoreData - Score data object
   * @returns {Promise<Object>} Created score object
   */
  async submitQuizScore(scoreData) {
    try {
      // Validate data
      if (!scoreData.userId || scoreData.score === undefined) {
        throw new Error('Invalid score data');
      }
      
      // Get user profile to retrieve university
      const userProfileRef = doc(db, 'profiles', scoreData.userId);
      const userProfileSnap = await getDoc(userProfileRef);
      const userProfile = userProfileSnap.data();
      
      // Create the score document
      const scoresCollection = collection(db, 'quizScores');
      const scoreDocRef = doc(scoresCollection);
      const timestamp = serverTimestamp();
      
      const scoreDoc = {
        userId: scoreData.userId,
        userName: userProfile?.basicInfo?.name || 'Anonymous',
        university: userProfile?.academicInfo?.university || null,
        score: scoreData.score,
        correctAnswers: scoreData.correctAnswers || 0,
        totalQuestions: scoreData.totalQuestions || 0,
        dateCompleted: timestamp,
        day: new Date().toISOString().split('T')[0] // Store YYYY-MM-DD format for easy querying
      };
      
      // Update the user's total score
      const userScoreRef = doc(db, 'userScores', scoreData.userId);
      const userScoreSnap = await getDoc(userScoreRef);
      
      // Transaction to update both documents atomically
      await runTransaction(db, async (transaction) => {
        // Add the new score
        transaction.set(scoreDocRef, scoreDoc);
        
        // Update or create user score aggregation
        if (userScoreSnap.exists()) {
          transaction.update(userScoreRef, {
            totalScore: increment(scoreData.score),
            quizCount: increment(1),
            lastUpdated: timestamp
          });
        } else {
          transaction.set(userScoreRef, {
            userId: scoreData.userId,
            userName: userProfile?.basicInfo?.name || 'Anonymous',
            totalScore: scoreData.score,
            quizCount: 1,
            university: userProfile?.academicInfo?.university || null,
            createdAt: timestamp,
            lastUpdated: timestamp
          });
        }
        
        // If user has a university, update the university rankings
        if (userProfile?.academicInfo?.university) {
          const universityRef = doc(db, 'universityRankings', userProfile.academicInfo.university);
          const universitySnap = await transaction.get(universityRef);
          
          if (universitySnap.exists()) {
            transaction.update(universityRef, {
              totalScore: increment(scoreData.score),
              participantCount: increment(userScoreSnap.exists() ? 0 : 1),
              lastUpdated: timestamp
            });
          } else {
            transaction.set(universityRef, {
              name: userProfile.academicInfo.university,
              totalScore: scoreData.score,
              participantCount: 1,
              createdAt: timestamp,
              lastUpdated: timestamp
            });
          }
        }
      });
      
      return {
        id: scoreDocRef.id,
        ...scoreDoc
      };
    } catch (error) {
      console.error('Error submitting quiz score:', error);
      throw error;
    }
  },
  
  /**
   * Fetches individual leaderboard data
   * @param {number} limit - Maximum number of entries to fetch
   * @returns {Promise<Array>} Array of leaderboard entries
   */
  async fetchLeaderboard(limitCount = 20) {
    try {
      const userScoresRef = collection(db, 'userScores');
      const leaderboardQuery = query(
        userScoresRef,
        orderBy('totalScore', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(leaderboardQuery);
        
      if (snapshot.empty) {
        return [];
      }
      
      // Process and rank entries
      const entries = snapshot.docs.map((doc, index) => {
        const data = doc.data();
        return {
          userId: data.userId,
          name: data.userName || 'Anonymous',
          avatar: data.avatar || null,
          university: data.university || null,
          totalScore: data.totalScore || 0,
          quizCount: data.quizCount || 0,
          rank: index + 1
        };
      });
      
      return entries;
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      return [];
    }
  },
  
  /**
   * Fetches university rankings
   * @param {number} limit - Maximum number of entries to fetch
   * @returns {Promise<Array>} Array of university ranking entries
   */
  async fetchUniversityRankings(limitCount = 20) {
    try {
      const uniRankingsRef = collection(db, 'universityRankings');
      const rankingsQuery = query(
        uniRankingsRef,
        orderBy('totalScore', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(rankingsQuery);
        
      if (snapshot.empty) {
        return [];
      }
      
      // Process and rank entries
      const entries = snapshot.docs.map((doc, index) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.name,
          totalScore: data.totalScore || 0,
          participants: data.participantCount || 0,
          rank: index + 1
        };
      });
      
      return entries;
    } catch (error) {
      console.error('Error fetching university rankings:', error);
      return [];
    }
  },
  
  /**
   * Fetches a user's total scores and stats
   * @param {string} userId - User ID to fetch scores for
   * @returns {Promise<Object|null>} User score data or null if not found
   */
  async fetchUserScores(userId) {
    try {
      if (!userId) return null;
      
      const userScoreRef = doc(db, 'userScores', userId);
      const userScoreSnap = await getDoc(userScoreRef);
      
      if (!userScoreSnap.exists()) {
        return {
          userId,
          totalScore: 0,
          quizCount: 0
        };
      }
      
      const userScoreData = userScoreSnap.data();
      
      // Fetch last 10 scores to calculate statistics
      const scoresRef = collection(db, 'quizScores');
      const scoresQuery = query(
        scoresRef,
        where('userId', '==', userId),
        orderBy('dateCompleted', 'desc'),
        limit(10)
      );
      
      const scoresSnapshot = await getDocs(scoresQuery);
        
      let avgScore = 0;
      let bestScore = 0;
      let completion = 0;
      
      if (!scoresSnapshot.empty) {
        const scores = scoresSnapshot.docs.map(doc => doc.data());
        
        // Calculate average percentage
        avgScore = Math.round(
          scores.reduce((acc, score) => acc + ((score.score / score.totalQuestions) * 10), 0) / scores.length
        );
        
        // Find best score
        bestScore = Math.max(...scores.map(score => score.score));
        
        // Calculate completion rate (percentage of questions answered)
        completion = Math.round(
          (scores.reduce((acc, score) => acc + score.correctAnswers, 0) / 
          scores.reduce((acc, score) => acc + score.totalQuestions, 0)) * 100
        );
      }
      
      return {
        ...userScoreData,
        stats: {
          avgScore,
          bestScore,
          completion
        }
      };
    } catch (error) {
      console.error('Error fetching user scores:', error);
      return null;
    }
  },
  
  /**
   * Gets a user's ranking position
   * @param {string} userId - User ID to get ranking for
   * @returns {Promise<number|null>} User's rank or null if not ranked
   */
  async getUserRankingPosition(userId) {
    try {
      if (!userId) return null;
      
      // Get user's score first
      const userScoreRef = doc(db, 'userScores', userId);
      const userScoreSnap = await getDoc(userScoreRef);
      
      if (!userScoreSnap.exists()) {
        return null;
      }
      
      const userScore = userScoreSnap.data().totalScore || 0;
      
      // Count number of users with higher scores
      const userScoresRef = collection(db, 'userScores');
      const higherScoresQuery = query(
        userScoresRef,
        where('totalScore', '>', userScore)
      );
      
      const higherScoresSnapshot = await getDocs(higherScoresQuery);
      
      // Rank is position + 1 (ranks start at 1)
      return higherScoresSnapshot.size + 1;
    } catch (error) {
      console.error('Error getting user ranking:', error);
      return null;
    }
  }
};

export default challengeService;